import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';

// Admin Navigation
export function AdminNav({ onLogout }) {
    return (
        <nav className="w-full bg-slate-900 border-b border-slate-800 sticky top-0 z-20 font-sans text-white">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <span className="text-2xl font-bold tracking-tight">VersantPro <span className="text-indigo-400">Admin</span></span>
                </div>
                <div className="flex items-center gap-4">
                    <span className="hidden md:inline-block text-xs font-mono text-amber-400 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20">
                        ⚡ Logout when finished to freeze background reads
                    </span>
                    <button
                        onClick={onLogout}
                        className="px-5 py-2.5 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white font-extrabold rounded-xl shadow-[0_0_20px_rgba(244,63,94,0.4)] transition-all hover:scale-105 text-sm flex items-center gap-2 cursor-pointer border border-rose-400/30"
                    >
                        <span>🚪</span> LOGOUT ADMIN
                    </button>
                </div>
            </div>
        </nav>
    );
}

// Admin Stats Card
export function AdminStatCard({ title, value, icon, color, subtitle }) {
    const colorClasses = {
        indigo: "bg-indigo-500/10 text-indigo-400",
        emerald: "bg-emerald-500/10 text-emerald-400",
        amber: "bg-amber-500/10 text-amber-400",
        rose: "bg-rose-500/10 text-rose-400",
        purple: "bg-purple-500/10 text-purple-400",
        sky: "bg-sky-500/10 text-sky-400",
    };

    return (
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color] || colorClasses.indigo}`}>
                    {icon}
                </div>
                {subtitle && (
                    <span className="text-xs font-mono bg-purple-500/10 text-purple-300 border border-purple-500/20 px-2.5 py-1 rounded-full">
                        {subtitle}
                    </span>
                )}
            </div>
            <div className="text-3xl font-bold text-white mb-1">{value}</div>
            <div className="text-sm text-slate-400 font-medium">{title}</div>
        </div>
    );
}

// User List Table
export function UserTable({ users, loading, premiumUserIds = new Set() }) {
    if (loading) {
        return <div className="text-center py-10 text-slate-400">Loading tracking grid...</div>;
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead className="bg-slate-900/50 text-xs uppercase text-slate-400 font-semibold border-b border-slate-700/50">
                    <tr>
                        <th className="px-6 py-4">Identity</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4">Total Actions</th>
                        <th className="px-6 py-4">Last Ping</th>
                        <th className="px-6 py-4">Current URL Path</th>
                        <th className="px-6 py-4">Live Track</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                    {users.map((session) => (
                        <tr key={session.userId} className="hover:bg-slate-800/50 transition-colors group">
                            <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                    <div className="relative">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-inner ${session.isRegistered ? 'bg-indigo-600' : 'bg-slate-600'}`}>
                                            {session.email[0].toUpperCase()}
                                        </div>
                                        {session.isOnline && (
                                            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-slate-900 rounded-full animate-pulse"></span>
                                        )}
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-white tracking-wide">
                                            {session.isRegistered ? session.email : 'Anonymous Guest'}
                                        </div>
                                        <div className="text-xs font-mono text-slate-500">{session.userId ? session.userId.slice(0, 15) : 'Unknown'}...</div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                {session.isRegistered ? (
                                    premiumUserIds.has(session.userId) ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded text-[10px] font-bold tracking-widest uppercase bg-yellow-500/20 text-yellow-500 border border-yellow-500/30">
                                            Premium User
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded text-[10px] font-bold tracking-widest uppercase bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                                            Free User
                                        </span>
                                    )
                                ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-[10px] font-bold tracking-widest uppercase bg-slate-700/30 text-slate-400 border border-slate-600/30">
                                        Web Traffic
                                    </span>
                                )}
                            </td>
                            <td className="px-6 py-4 text-sm font-mono text-slate-300">
                                {session.totalClicks} <span className="text-slate-500 text-xs font-sans">clicks</span>
                            </td>
                            <td className="px-6 py-4 text-sm font-mono text-slate-400">
                                {new Date(session.lastActive).toLocaleTimeString()}
                            </td>
                            <td className="px-6 py-4">
                                <span className="text-xs font-mono text-emerald-400 bg-emerald-900/10 px-2 py-1 rounded inline-block truncate max-w-[150px]">
                                    {session.currentPath}
                                </span>
                            </td>
                            <td className="px-6 py-4">
                                <button
                                    onClick={() => window.dispatchEvent(new CustomEvent('open-user-modal', { detail: session }))}
                                    className="px-3 py-1.5 bg-slate-800 hover:bg-indigo-600 rounded text-xs font-bold text-white transition-colors"
                                >
                                    Inspect Route
                                </button>
                            </td>
                        </tr>
                    ))}
                    {users.length === 0 && (
                        <tr>
                            <td colSpan="6" className="px-6 py-12 text-center text-slate-500 font-mono">
                                No radar contacts found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

// Detailed Session Tracking Modal
export function UserDetailsModal({ user: session, onClose }) {
    const [testResults, setTestResults] = useState([]);
    const [loadingResults, setLoadingResults] = useState(true);

    useEffect(() => {
        if (!session?.userId) return;
        const fetchUserResults = async () => {
            setLoadingResults(true);
            try {
                const resultsRef = collection(db, 'users', session.userId, 'testResults');
                const snap = await getDocs(resultsRef);
                const list = [];
                snap.forEach(docSnap => {
                    list.push({ id: docSnap.id, ...docSnap.data() });
                });
                list.sort((a, b) => {
                    const timeA = new Date(a.timestamp || a.createdAt?.toDate?.() || a.createdAt || 0).getTime();
                    const timeB = new Date(b.timestamp || b.createdAt?.toDate?.() || b.createdAt || 0).getTime();
                    return timeB - timeA;
                });
                setTestResults(list);
            } catch (err) {
                console.error("Error fetching user test results in admin modal:", err);
            } finally {
                setLoadingResults(false);
            }
        };
        fetchUserResults();
    }, [session?.userId]);

    if (!session) return null;

    // session.history contains the chronological array of analytics_events
    const history = [...(session.history || [])].sort((a,b) => b.time - a.time);

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm font-sans">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-[0_0_50px_rgba(0,0,0,0.5)]">
                {/* Header */}
                <div className="p-6 border-b border-slate-800 flex justify-between items-start bg-slate-900 sticky top-0 z-10">
                    <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold text-white shadow-lg ${session.isRegistered ? 'bg-indigo-600' : 'bg-slate-700'}`}>
                            {session.email[0].toUpperCase()}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl font-bold text-white">{session.isRegistered ? session.email : 'Anonymous Web Guest'}</h2>
                                {session.isOnline && (
                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold tracking-widest uppercase bg-green-500/20 text-green-400 border border-green-500/30 animate-pulse">
                                        Live Now
                                    </span>
                                )}
                            </div>
                            <p className="text-slate-400 font-mono text-xs mt-1">ID: {session.userId}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto bg-slate-950 flex-1 space-y-6">
                    
                    {/* Security Info */}
                    <div className="bg-slate-900/80 p-4 rounded-xl border border-slate-800 flex justify-between items-center">
                        <div>
                            <div className="text-slate-500 text-xs font-bold tracking-widest uppercase mb-1">Session Data</div>
                            <div className="text-white font-mono text-sm">{history.length} Event Packets • {testResults.length} Tests Completed</div>
                        </div>
                        <div className="text-right">
                            <div className="text-slate-500 text-xs font-bold tracking-widest uppercase mb-1">User Agent</div>
                            <div className="text-slate-400 font-mono text-xs truncate max-w-[200px]" title={history[0]?.userAgent}>{history[0]?.userAgent || 'Unknown System'}</div>
                        </div>
                    </div>

                    {/* Test History Section */}
                    <div>
                        <h3 className="text-sm font-bold text-slate-300 tracking-widest uppercase mb-4 flex items-center gap-2">
                            <span>🏆</span> Test Performance & Scores ({testResults.length})
                        </h3>

                        {loadingResults ? (
                            <div className="text-xs text-slate-500 font-mono py-4">Loading user test records...</div>
                        ) : testResults.length === 0 ? (
                            <div className="bg-slate-900/60 p-4 rounded-xl border border-slate-800 text-xs text-slate-400 font-mono">
                                No completed test records found in Firestore for this user account.
                            </div>
                        ) : (
                            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-slate-800/80 text-slate-400 font-semibold uppercase tracking-wider">
                                        <tr>
                                            <th className="p-3">Test Name</th>
                                            <th className="p-3">Test Type</th>
                                            <th className="p-3">Score</th>
                                            <th className="p-3">Date Completed</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800/60">
                                        {testResults.map((t, idx) => {
                                            const score = t.totalScore || 0;
                                            const scoreColor = score >= 70 ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' : score >= 60 ? 'text-amber-400 bg-amber-500/10 border-amber-500/30' : 'text-rose-400 bg-rose-500/10 border-rose-500/30';
                                            const rawDate = t.timestamp || t.createdAt;
                                            const dateStr = rawDate ? new Date(rawDate).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';
                                            return (
                                                <tr key={t.id || idx} className="hover:bg-slate-800/40">
                                                    <td className="p-3 font-bold text-white">
                                                        {t.testId && t.testId !== 'unknown' ? `Test ${t.testId}` : 'Versant Practice'}
                                                    </td>
                                                    <td className="p-3 text-slate-300 capitalize">
                                                        {t.type ? t.type.replace(/_/g, ' ') : 'Full Test'}
                                                    </td>
                                                    <td className="p-3">
                                                        <span className={`px-2.5 py-1 rounded font-extrabold border ${scoreColor}`}>
                                                            {score} / 80
                                                        </span>
                                                    </td>
                                                    <td className="p-3 text-slate-400 font-mono text-[11px]">
                                                        {dateStr}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* True Chronological Timeline */}
                    <div>
                        <h3 className="text-sm font-bold text-slate-300 tracking-widest uppercase mb-4 flex items-center gap-2">
                            <span>📡</span> Real-Time Action Log
                        </h3>
                        
                        <div className="relative pl-4 border-l-2 border-slate-800/80 space-y-4">
                            {history.map((evt, idx) => {
                                const isLatest = idx === 0;
                                return (
                                    <div key={evt.id || idx} className="relative group">
                                        <div className={`absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-slate-950 ${isLatest ? 'bg-emerald-500 shadow-[0_0_10px_#10b981]' : 'bg-slate-600'}`}></div>
                                        
                                        <div className="flex gap-4 items-start">
                                            <div className="w-20 pt-0.5 text-xs font-mono text-slate-500 flex-shrink-0">
                                                {evt.time.toLocaleTimeString()}
                                            </div>
                                            <div className={`flex-1 rounded-lg p-3 border border-transparent group-hover:bg-slate-900 group-hover:border-slate-800 transition-colors ${isLatest ? 'bg-slate-900/50 border-slate-800/50' : ''}`}>
                                                <div className="text-xs font-bold tracking-wider text-indigo-400 mb-1">[{evt.eventType}]</div>
                                                <div className="text-sm text-white font-mono flex items-center gap-2">
                                                    <span className="text-slate-500">→</span> {evt.path}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
